/**
 * CepInput — campo de CEP com máscara, validação e auto-lookup via ViaCEP.
 *
 * Quando o usuário digita um CEP válido (8 dígitos), busca automaticamente
 * o endereço no ViaCEP (API pública, sem chave) e chama `onAutoFill` com
 * `{ logradouro, bairro, localidade (cidade), uf, ibge }` para o formulário
 * pré-preencher os campos correspondentes.
 *
 * Props:
 *  - value, onChange(masked): controle do campo
 *  - onAutoFill({ logradouro, bairro, localidade, uf, ibge }): callback
 *    chamado quando o ViaCEP retorna sucesso. Use para preencher os outros
 *    campos do formulário.
 *  - required: exibe asterisco vermelho
 *  - testId: data-testid customizado
 */
import React, { useState } from 'react';
import { MapPin, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const formatCep = (v) => {
  const d = String(v || '').replace(/\D/g, '').slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

export const stripCep = (v) => String(v || '').replace(/\D/g, '');

export const isValidCepFormat = (v) => stripCep(v).length === 8;

export default function CepInput({
  value,
  onChange,
  onAutoFill,
  required = false,
  testId = 'cep-input',
  label = 'CEP',
  placeholder = '00000-000',
}) {
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const lookup = async (digits) => {
    setStatus('loading');
    setErrorMsg('');
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      if (!resp.ok) throw new Error('viacep_unreachable');
      const data = await resp.json();
      if (data.erro) {
        setStatus('error');
        setErrorMsg('CEP não encontrado');
        return;
      }
      setStatus('success');
      onAutoFill?.({
        logradouro: data.logradouro || '',
        bairro: data.bairro || '',
        localidade: data.localidade || '',
        uf: data.uf || '',
        ibge: data.ibge || '',
        complemento: data.complemento || '',
      });
    } catch (e) {
      setStatus('error');
      setErrorMsg('Falha ao consultar CEP. Tente novamente.');
    }
  };

  const handleChange = (v) => {
    const masked = formatCep(v);
    onChange(masked);
    setStatus('idle');
    setErrorMsg('');
    const digits = stripCep(masked);
    if (digits.length === 8) {
      lookup(digits);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          inputMode="numeric"
          maxLength={9}
          className={`w-full pl-10 pr-10 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] ${
            status === 'error' ? 'border-red-400' : 'border-[var(--border)]'
          }`}
          data-testid={testId}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {status === 'loading' && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          {status === 'success' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
          {status === 'error' && <AlertCircle className="w-4 h-4 text-red-500" />}
        </div>
      </div>
      {status === 'error' && (
        <p className="text-xs text-red-500 mt-1" data-testid={`${testId}-error`}>{errorMsg}</p>
      )}
      {status === 'success' && (
        <p className="text-xs text-green-600 mt-1" data-testid={`${testId}-success`}>
          Endereço encontrado e preenchido automaticamente.
        </p>
      )}
      {status === 'idle' && (
        <p className="text-xs text-gray-500 mt-1">
          Digite o CEP para preencher rua, bairro e cidade automaticamente.
        </p>
      )}
    </div>
  );
}
